import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import config from '../config/mastermind';
import './Mastermind.css';

// Work out where "now" falls relative to the configured session.
// Returns one of three states:
//   'expired'  – meeting ended more than graceMinutes ago (or no link set)  → fallback page
//   'live'     – within [start - lead, start + grace]                        → auto-redirect
//   'upcoming' – more than `lead` minutes before the start                   → show details + Join now
function getSessionState({ zoomUrl, meetsAt, redirectLeadMinutes, graceMinutes }) {
  if (!zoomUrl) return 'expired';

  const start = new Date(meetsAt);
  if (Number.isNaN(start.getTime())) return 'expired'; // malformed date → be safe

  const now = Date.now();
  const redirectStart = start.getTime() - (redirectLeadMinutes ?? 0) * 60 * 1000;
  const redirectEnd = start.getTime() + (graceMinutes ?? 0) * 60 * 1000;

  if (now > redirectEnd) return 'expired';
  if (now >= redirectStart) return 'live';
  return 'upcoming';
}

// Format the meeting time in the meeting's own timezone so every visitor sees
// the same wall-clock time (e.g. "Thursday, July 23, 2026 at 5:00 PM MDT").
function formatMeetingTime({ meetsAt, timeZone }) {
  const start = new Date(meetsAt);
  if (Number.isNaN(start.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: timeZone || undefined,
    }).format(start);
  } catch (e) {
    // Invalid timeZone string → fall back to the visitor's local formatting.
    return start.toLocaleString();
  }
}

const Mastermind = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const state = getSessionState(config);

  useEffect(() => {
    setIsLoaded(true);
    if (state === 'live') {
      // replace() so the browser Back button returns to wherever they came
      // from rather than re-triggering this redirect.
      window.location.replace(config.zoomUrl);
    }
  }, [state]);

  const meetingTime = formatMeetingTime(config);

  const page = (content) => (
    <div className={`page mastermind-page ${isLoaded ? 'loaded' : ''}`}>
      <section className="mastermind-section">
        <div className="container mastermind-inner">{content}</div>
      </section>
    </div>
  );

  if (state === 'expired') {
    return page(
      <>
        <span className="label">Mastermind</span>
        <h1>No session scheduled right now</h1>
        <p>
          There isn't a live mastermind at the moment. Reach out and we'll make
          sure you're on the list for the next one.
        </p>
        <div className="mastermind-actions">
          <Link to="/contact" className="btn btn-primary">Get in touch</Link>
        </div>
      </>
    );
  }

  if (state === 'live') {
    return page(
      <>
        <span className="label">Mastermind</span>
        <h1>Opening Zoom&hellip;</h1>
        <p>
          We're launching your session now. If it doesn't open automatically,
          use the button below.
        </p>
        <div className="mastermind-actions">
          <a href={config.zoomUrl} className="btn btn-primary">Join now</a>
        </div>
      </>
    );
  }

  // 'upcoming'
  return page(
    <>
      <span className="label">Mastermind</span>
      <h1>Your next session</h1>
      {meetingTime && <p className="mastermind-time">{meetingTime}</p>}
      <p>
        This page will open Zoom automatically starting {config.redirectLeadMinutes}{' '}
        minutes before we begin. You can also join manually any time using the
        button below.
      </p>
      <div className="mastermind-actions">
        <a href={config.zoomUrl} className="btn btn-primary">Join now</a>
      </div>
    </>
  );
};

export default Mastermind;
